import React, { useState, useEffect, useCallback } from 'react';
import { Plus, CheckCircle2, Circle, Trash2, Loader2 } from 'lucide-react';
import api from '../../utils/api';
import { getLocalDateInputValue, formatRecordDate } from '../../utils/dateUtils';
import toast from 'react-hot-toast';

const AssetsTab = ({ personnelId, onAssetsChange }) => {
    const [assets, setAssets] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [form, setForm] = useState({ itemName: '', givenDate: getLocalDateInputValue(), note: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchAssets = useCallback(async () => {
        setIsLoading(true);
        try {
            const res = await api.get(`/personnel/${personnelId}/assets`);
            setAssets(res.data || []);
            onAssetsChange?.(res.data || []);
        } catch (error) {
            toast.error('Zimmet yüklenemedi.');
        } finally {
            setIsLoading(false);
        }
    }, [personnelId, onAssetsChange]);

    useEffect(() => { fetchAssets(); }, [fetchAssets]);

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!form.itemName.trim()) {
            toast.error('Eşya adı zorunludur.');
            return;
        }
        setIsSubmitting(true);
        try {
            await api.post(`/personnel/${personnelId}/assets`, form);
            toast.success('Zimmet eklendi.');
            setForm({ itemName: '', givenDate: getLocalDateInputValue(), note: '' });
            fetchAssets();
        } catch (error) {
            toast.error(error.response?.data?.message || 'Zimmet eklenemedi.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleReturn = async (asset) => {
        try {
            await api.patch(`/personnel/assets/${asset.id}/return`);
            fetchAssets();
        } catch (error) {
            toast.error('Durum güncellenemedi.');
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu zimmet kaydını silmek istediğinize emin misiniz?')) return;
        try {
            await api.delete(`/personnel/assets/${id}`);
            toast.success('Zimmet silindi.');
            fetchAssets();
        } catch (error) {
            toast.error('Silinemedi.');
        }
    };

    const inputClass = 'px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg focus:outline-none focus:border-primary-500 text-white text-sm';

    return (
        <div className="space-y-5">
            {/* Ekleme formu */}
            <form onSubmit={handleAdd} className="glass border border-dark-border rounded-xl p-4 grid grid-cols-2 md:grid-cols-4 gap-3 items-end">
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Eşya Adı</label>
                    <input type="text" value={form.itemName} onChange={(e) => setForm({ ...form, itemName: e.target.value })} className={inputClass} placeholder="Örn: Telefon, Anahtar" />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Veriliş Tarihi</label>
                    <input type="date" value={form.givenDate} onChange={(e) => setForm({ ...form, givenDate: e.target.value })} className={inputClass} />
                </div>
                <div className="flex flex-col gap-1">
                    <label className="text-xs text-dark-muted">Not</label>
                    <input type="text" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={inputClass} placeholder="Opsiyonel" />
                </div>
                <div>
                    <button type="submit" disabled={isSubmitting} className="w-full flex items-center justify-center gap-1 bg-primary-600 hover:bg-primary-500 text-white px-3 py-2 rounded-lg text-sm font-semibold disabled:opacity-50">
                        <Plus size={16} /> Ekle
                    </button>
                </div>
            </form>

            {/* Liste */}
            <div className="glass border border-dark-border rounded-xl overflow-hidden">
                <div className="mobile-table-hint lg:hidden">Tüm zimmet ayrıntıları için tabloyu yatay kaydırın →</div>
                <div className="mobile-table-scroll overflow-x-auto">
                    <table className="mobile-complete-table w-full text-left text-sm whitespace-nowrap">
                        <thead className="bg-dark-card text-dark-muted border-b border-dark-border uppercase tracking-wider text-[11px]">
                            <tr>
                                <th className="px-4 py-3 font-semibold">Eşya</th>
                                <th className="px-4 py-3 font-semibold">Veriliş</th>
                                <th className="px-4 py-3 font-semibold text-center">Teslim Alındı</th>
                                <th className="px-4 py-3 font-semibold">İade Tarihi</th>
                                <th className="px-4 py-3 font-semibold">Not</th>
                                <th className="px-4 py-3 font-semibold text-right">İşlem</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-dark-border">
                            {isLoading ? (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-dark-muted"><Loader2 className="animate-spin inline" size={20} /></td></tr>
                            ) : assets.length === 0 ? (
                                <tr><td colSpan="6" className="px-4 py-8 text-center text-dark-muted">Zimmet kaydı bulunamadı.</td></tr>
                            ) : (
                                assets.map((a) => (
                                    <tr key={a.id} className="hover:bg-dark-hover transition-colors">
                                        <td className="px-4 py-3 font-semibold text-dark-text">{a.itemName}</td>
                                        <td className="px-4 py-3 text-dark-muted">{a.givenDate ? formatRecordDate(a.givenDate) : '-'}</td>
                                        <td className="px-4 py-3 text-center">
                                            <button onClick={() => handleToggleReturn(a)} className={a.isReturned ? 'text-green-500' : 'text-dark-muted hover:text-dark-text'} title={a.isReturned ? 'Teslim alındı' : 'Zimmette'}>
                                                {a.isReturned ? <CheckCircle2 size={22} /> : <Circle size={22} />}
                                            </button>
                                        </td>
                                        <td className="px-4 py-3 text-dark-muted">{a.returnDate ? formatRecordDate(a.returnDate) : '-'}</td>
                                        <td className="px-4 py-3 text-dark-muted max-w-[160px] truncate" title={a.note}>{a.note || '-'}</td>
                                        <td className="px-4 py-3 text-right">
                                            <button onClick={() => handleDelete(a.id)} className="p-1.5 text-dark-muted hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-colors">
                                                <Trash2 size={16} />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default AssetsTab;
